/**
 * Centralized logging utility with external service integration
 *
 * Features:
 * - Different log levels (debug, info, warn, error)
 * - Development vs production modes
 * - Log buffering for debugging
 * - External service integration (Sentry, LogRocket, etc.)
 * - Structured logging with context
 * - Performance tracking
 * - Scoped loggers
 */

type LogLevel = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR';

interface LogContext {
  [key: string]: any;
}

interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: string;
  context?: LogContext;
  stack?: string;
}

interface LoggerConfig {
  enableDebug: boolean;
  enableInfo: boolean;
  prefix?: string;
  bufferSize?: number;
}

class Logger {
  private config: LoggerConfig;
  private logBuffer: LogEntry[] = [];
  private isDevelopment: boolean;
  private isProduction: boolean;

  constructor(config?: Partial<LoggerConfig>) {
    this.isDevelopment = process.env.NODE_ENV === 'development';
    this.isProduction = process.env.NODE_ENV === 'production';

    this.config = {
      enableDebug: this.isDevelopment || process.env.REACT_APP_DEBUG === 'true',
      enableInfo: this.isDevelopment,
      bufferSize: 100,
      ...config,
    };
  }

  /**
   * Debug level logging - only in development
   * Accepts variable arguments for flexibility
   */
  debug(message: string, ...args: any[]): void {
    const context = this.parseArgs(args);

    if (this.config.enableDebug) {
      // eslint-disable-next-line no-console
      console.debug(this.formatMessage('DEBUG', message), context);
    }
    this.addToBuffer('DEBUG', message, context);
  }

  /**
   * Info level logging
   * Accepts variable arguments for flexibility
   */
  info(message: string, ...args: any[]): void {
    const context = this.parseArgs(args);

    if (this.config.enableInfo) {
      // eslint-disable-next-line no-console
      console.info(this.formatMessage('INFO', message), context);
    }
    this.addToBuffer('INFO', message, context);

    if (this.isProduction) {
      this.sendToExternalService('INFO', message, context);
    }
  }

  /**
   * Warning level logging
   * Accepts variable arguments for flexibility
   */
  warn(message: string, ...args: any[]): void {
    const context = this.parseArgs(args);

    // eslint-disable-next-line no-console
    console.warn(this.formatMessage('WARN', message), context);
    this.addToBuffer('WARN', message, context);

    if (this.isProduction) {
      this.sendToExternalService('WARN', message, context);
    }
  }

  /**
   * Parse variable arguments into LogContext
   * Supports: primitives, objects, arrays
   */
  private parseArgs(args: any[]): LogContext {
    if (args.length === 0) {
      return {};
    }

    // If first arg is already an object (and not array), use it as context
    if (
      args.length === 1 &&
      typeof args[0] === 'object' &&
      !Array.isArray(args[0]) &&
      args[0] !== null
    ) {
      return args[0];
    }

    // If first arg is primitive or there are multiple args, wrap them
    if (args.length === 1) {
      return { data: args[0] };
    }

    // Multiple args - return as array
    return { args };
  }

  /**
   * Error level logging
   * Accepts variable arguments for flexibility:
   * - error(message)
   * - error(message, error)
   * - error(message, error, context)
   * - error(message, ...any[]) for compatibility
   */
  error(message: string, ...args: any[]): void {
    let error: Error | unknown | undefined;
    let context: LogContext = {};

    // First arg might be an Error or error object
    if (
      args.length > 0 &&
      (args[0] instanceof Error || (args[0] && typeof args[0] === 'object' && 'message' in args[0]))
    ) {
      error = args[0];
      // Check if there's a context object as second arg
      if (args.length > 1) {
        context = this.parseArgs(args.slice(1));
      }
    } else {
      // No error object, just context args
      context = this.parseArgs(args);
    }

    const errorContext = {
      ...context,
      errorMessage:
        error instanceof Error
          ? error.message
          : error && typeof error === 'object' && 'message' in error
            ? (error as any).message
            : error
              ? String(error)
              : undefined,
      errorStack: error instanceof Error ? error.stack : undefined,
    };

    // eslint-disable-next-line no-console
    console.error(this.formatMessage('ERROR', message), errorContext);
    if (error instanceof Error && error.stack) {
      // eslint-disable-next-line no-console
      console.error(error.stack);
    }

    this.addToBuffer(
      'ERROR',
      message,
      errorContext,
      error instanceof Error ? error.stack : undefined
    );

    if (this.isProduction) {
      this.sendToExternalService('ERROR', message, errorContext);
      this.sendToErrorTracking(message, error, context);
    }
  }

  /**
   * Performance tracking
   */
  performance(label: string, startTime: number, context?: LogContext): void {
    const duration = performance.now() - startTime;
    const message = `${label} took ${duration.toFixed(2)}ms`;

    if (this.config.enableDebug) {
      // eslint-disable-next-line no-console
      console.log(this.formatMessage('INFO', `[PERF] ${message}`), context || '');
    }

    this.addToBuffer('INFO', message, { ...context, duration, label, type: 'performance' });
  }

  /**
   * Group logging for related operations
   */
  group(label: string): void {
    if (this.config.enableDebug) {
      // eslint-disable-next-line no-console
      console.group(label);
    }
  }

  groupEnd(): void {
    if (this.config.enableDebug) {
      // eslint-disable-next-line no-console
      console.groupEnd();
    }
  }

  /**
   * Table logging for arrays/objects
   */
  table(data: any, columns?: string[]): void {
    if (this.config.enableDebug) {
      // eslint-disable-next-line no-console
      console.table(data, columns);
    }
  }

  /**
   * Add log entry to buffer
   */
  private addToBuffer(
    level: LogLevel,
    message: string,
    context?: LogContext,
    stack?: string
  ): void {
    const entry: LogEntry = {
      level,
      message,
      timestamp: new Date().toISOString(),
      context,
      stack,
    };

    this.logBuffer.push(entry);

    // Maintain buffer size
    if (this.logBuffer.length > (this.config.bufferSize || 100)) {
      this.logBuffer.shift();
    }
  }

  /**
   * Get recent logs (useful for debugging)
   */
  getRecentLogs(count: number = 50): LogEntry[] {
    return this.logBuffer.slice(-count);
  }

  /**
   * Clear log buffer
   */
  clearBuffer(): void {
    this.logBuffer = [];
  }

  /**
   * Format log message with timestamp and level
   */
  private formatMessage(level: LogLevel, message: string): string {
    const timestamp = new Date().toISOString();
    const prefix = this.config.prefix ? `[${this.config.prefix}]` : '';
    return `[${timestamp}] [${level}]${prefix ? ' ' + prefix : ''} ${message}`;
  }

  /**
   * Send to external logging service (e.g., LogRocket, Datadog)
   */
  private sendToExternalService(_level: LogLevel, _message: string, _context?: LogContext): void {
    // TODO: Implement integration with external logging service
    // Example: LogRocket.log(level.toLowerCase(), message, context);
    // Example: window.datadog?.logger[level.toLowerCase()](message, context);
  }

  /**
   * Send to error tracking service (e.g., Sentry)
   */
  private sendToErrorTracking(
    _message: string,
    _error?: Error | unknown,
    _context?: LogContext
  ): void {
    // TODO: Implement Sentry integration
    // Example:
    // if (window.Sentry) {
    //   window.Sentry.captureException(error, {
    //     tags: { type: 'application-error' },
    //     extra: { message, ...context }
    //   });
    // }
  }

  /**
   * Create a scoped logger with default context
   */
  createScoped(scope: string, _defaultContext?: LogContext): Logger {
    return new Logger({
      ...this.config,
      prefix: this.config.prefix ? `${this.config.prefix}:${scope}` : scope,
    });
  }
}

// Export singleton instance
export const logger = new Logger();

// Export factory function for scoped loggers
export const createLogger = (prefix: string, _context?: LogContext) => new Logger({ prefix });

// Export types
export type { LogContext, LogLevel, LogEntry, LoggerConfig };
